import React, { useEffect, useState, useRef } from "react";
import "./AdminMaps.css";
import { useNavigate } from "react-router-dom";
import bfLogo from "../assets/bf_logo.png";
import { History, Map as MapIcon, Layers } from "lucide-react";
import { useTranslation } from "react-i18next";

/* ===== MAPBOX ===== */
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

/* ===== FIREBASE ===== */
import { db, auth } from "../firebase";
import {
  collection,
  onSnapshot,
  query,
  where,
  doc,
  getDoc,
  orderBy,
  getDocs,
  limit
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

mapboxgl.accessToken =
  "pk.eyJ1IjoiYnVpbGRmb3JjZWFsIiwiYSI6ImNtbGNlOThzZzAwNm4zaHM4NnNtdWllZDIifQ.bQWAZEdO_21-pnCxk7KpFg";

/* ---------------- HELPERS ---------------- */
const pad = (n) => String(n).padStart(2, "0");

const formatSecondsToTime = (totalSeconds) => {
  if (!totalSeconds || totalSeconds < 0) return "00:00";
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  return `${pad(h)}:${pad(m)}`;
};

const formatTime = (timestamp) => {
  if (!timestamp) return "--:--";
  try {
    if (timestamp && typeof timestamp.toDate === "function") {
      return timestamp.toDate().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
    if (typeof timestamp === "number") {
      return new Date(timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
    if (typeof timestamp === "string") {
      if (timestamp.includes("T") || timestamp.includes("-")) {
        const date = new Date(timestamp);
        if (!isNaN(date.getTime())) return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      }
      if (timestamp.includes(":")) return timestamp.length > 5 ? timestamp.substring(0, 5) : timestamp;
    }
  } catch {
    return "--:--";
  }
  return String(timestamp).substring(0, 5);
};

const timeStringToMin = (str) => {
  if (!str || typeof str !== "string" || !str.includes(":")) return 0;
  const [h, m] = str.split(":").map(Number);
  return h * 60 + m;
};

const getWeekNumberISO = (date = new Date()) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
};

const formatTopDate = (d = new Date()) =>
  d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }).toLowerCase();

const weatherEmoji = (main = "", icon = "") => {
  const m = String(main).toLowerCase();
  if (m.includes("thunder")) return "⛈️";
  if (m.includes("drizzle")) return "🌦️";
  if (m.includes("rain")) return "🌧️";
  if (m.includes("snow")) return "❄️";
  if (m.includes("mist") || m.includes("fog") || m.includes("haze") || m.includes("smoke")) return "🌫️";
  if (m.includes("cloud")) return "☁️";
  if (icon.includes("n")) return "🌙";
  return "☀️";
};

const firstUpper = (s = "") => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

const tsToMs = (v) => {
  if (!v) return 0;
  if (typeof v === "number") return v;
  if (typeof v?.toDate === "function") return v.toDate().getTime();
  if (typeof v === "string") {
    const t = new Date(v).getTime();
    return isNaN(t) ? 0 : t;
  }
  return 0;
};

const isoDate = (d = new Date()) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const sameCoord = (a, b) => {
  if (!a || !b) return false;
  return Math.abs(a[0] - b[0]) < 0.000001 && Math.abs(a[1] - b[1]) < 0.000001;
};

/* ===== ROUTE COLORING + LOST ===== */
const PALETTE = [
  "#3b82f6",
  "#a855f7",
  "#06b6d4",
  "#f59e0b",
  "#10b981",
  "#ef4444",
  "#8b5cf6",
  "#14b8a6",
  "#eab308",
  "#ec4899",
  "#84cc16",
  "#f97316"
];

const hashString = (str = "") => {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
};

const workerColor = (workerId) => PALETTE[hashString(workerId) % PALETTE.length];
const LOST_GAP_SEC = 10 * 60;

const distanceMeters = (a, b) => {
  if (!a || !b) return 0;
  const toRad = (x) => (x * Math.PI) / 180;
  const [lng1, lat1] = a;
  const [lng2, lat2] = b;
  const R = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const s1 = Math.sin(dLat / 2);
  const s2 = Math.sin(dLng / 2);
  const q = s1 * s1 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * s2 * s2;
  const c = 2 * Math.atan2(Math.sqrt(q), Math.sqrt(1 - q));
  return R * c;
};

const toTsSeconds = (v) => {
  if (!v) return 0;
  if (typeof v === "number") return Math.floor(v / 1000);
  if (typeof v?.toDate === "function") return Math.floor(v.toDate().getTime() / 1000);
  if (typeof v === "string") {
    const t = new Date(v).getTime();
    return Number.isNaN(t) ? 0 : Math.floor(t / 1000);
  }
  return 0;
};

const calcLostStatsFromPoints = (pointsSec = [], nowSec = 0, appendOpenGap = false) => {
  if (!Array.isArray(pointsSec) || pointsSec.length < 2) {
    return { lostTotalSec: appendOpenGap ? Math.max(0, nowSec - (pointsSec[0] || nowSec)) : 0, lostEvents: 0 };
  }
  let lostTotalSec = 0;
  let lostEvents = 0;
  for (let i = 1; i < pointsSec.length; i += 1) {
    const gap = Math.max(0, pointsSec[i] - pointsSec[i - 1]);
    if (gap >= LOST_GAP_SEC) {
      lostTotalSec += gap;
      lostEvents += 1;
    }
  }
  if (appendOpenGap) {
    const last = pointsSec[pointsSec.length - 1] || nowSec;
    const openGap = Math.max(0, nowSec - last);
    if (openGap >= LOST_GAP_SEC) {
      lostTotalSec += openGap;
      lostEvents += 1;
    }
  }
  return { lostTotalSec, lostEvents };
};

export default function AdminMaps() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const mapContainer = useRef(null);
  const map = useRef(null);
  const markersRef = useRef({});
  const currentPopup = useRef(null);
  const endpointMarkersRef = useRef({});
  const historyPointsRef = useRef([]);
  const workersByIdRef = useRef({});
  const routeLayerIdsRef = useRef(new Set());

  // ✅ Globe spin control refs
  const spinEnabledRef = useRef(true);
  const spinFrameRef = useRef(null);
  const userInteractedRef = useRef(false);

  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);

  const [workersList, setWorkersList] = useState([]);
  const [allWorkers, setAllWorkers] = useState([]);

  const [showHistoryPanel, setShowHistoryPanel] = useState(false);

  const [mapStyle, setMapStyle] = useState("mapbox://styles/mapbox/streets-v12");
  const [liveRouteData, setLiveRouteData] = useState({});
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  const [selectedWorkerId, setSelectedWorkerId] = useState("");
  const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const [topDateText, setTopDateText] = useState(formatTopDate(new Date()));
  const [topKW, setTopKW] = useState(pad(getWeekNumberISO(new Date())));

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        const userSnap = await getDoc(doc(db, "users", user.uid));
        if (userSnap.exists()) setUserData(userSnap.data());
      } else {
        navigate("/admin/login");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    const tick = () => {
      const d = new Date();
      setTopDateText(formatTopDate(d));
      setTopKW(pad(getWeekNumberISO(d)));
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, []);

  const masterId = userData?.belongsTo || currentUser?.uid;

  useEffect(() => {
    if (map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: mapStyle,
      center: [20, 50],
      zoom: 1.5,
      projection: "globe",
      pitch: 45,
      bearing: 0,
      antialias: true
    });

    map.current.on("load", () => {
      map.current.addControl(new mapboxgl.NavigationControl(), "bottom-right");
      setIsMapLoaded(true);
      refreshMapLayers();
      forceBlackHouseNumbers();
      spinGlobe();
    });

    map.current.on("style.load", () => {
      refreshMapLayers();
      forceBlackHouseNumbers();
    });
  }, []);

  const runWhenMapStyleReady = (cb) => {
    if (!map.current) return;
    if (map.current.isStyleLoaded()) {
      cb();
      return;
    }
    const onLoad = () => cb();
    map.current.once("styledata", onLoad);
  };

  const forceBlackHouseNumbers = () => {
    if (!map.current) return;
    const layers = map.current.getStyle()?.layers || [];
    layers.forEach((layer) => {
      if (
        layer.type === "symbol" &&
        (layer.id.includes("house") || layer.id.includes("num") || layer.id.includes("address"))
      ) {
        try {
          map.current.setPaintProperty(layer.id, "text-color", "#000000");
          map.current.setPaintProperty(layer.id, "text-halo-color", "#ffffff");
          map.current.setPaintProperty(layer.id, "text-halo-width", 2);
          map.current.setPaintProperty(layer.id, "text-opacity", 1);
        } catch {}
      }
    });
  };

  const spinGlobe = () => {
    if (!map.current) return;

    const secondsPerRevolution = 240;
    const maxSpinZoom = 5;
    const slowSpinZoom = 3;

    // register once
    if (!map.current.__spinListenersAttached) {
      map.current.on("mousedown", () => {
        userInteractedRef.current = true;
      });
      map.current.on("wheel", () => {
        userInteractedRef.current = true;
      });
      map.current.__spinListenersAttached = true;
    }

    if (spinFrameRef.current) {
      cancelAnimationFrame(spinFrameRef.current);
      spinFrameRef.current = null;
    }

    const spin = () => {
      if (!map.current || !spinEnabledRef.current) return;

      const zoom = map.current.getZoom();
      if (!userInteractedRef.current && zoom < maxSpinZoom) {
        let distancePerSecond = 360 / secondsPerRevolution;
        if (zoom > slowSpinZoom) {
          distancePerSecond *= (maxSpinZoom - zoom) / (maxSpinZoom - slowSpinZoom);
        }
        const center = map.current.getCenter();
        center.lng -= distancePerSecond / 60;
        map.current.jumpTo({ center });
      }
      spinFrameRef.current = requestAnimationFrame(spin);
    };

    spinFrameRef.current = requestAnimationFrame(spin);
  };

  useEffect(() => {
    if (!map.current) return;

    if (showHistoryPanel) {
      // ✅ stop spinning in History
      spinEnabledRef.current = false;
      if (spinFrameRef.current) {
        cancelAnimationFrame(spinFrameRef.current);
        spinFrameRef.current = null;
      }
    } else {
      // ✅ resume spinning in Live
      spinEnabledRef.current = true;
      userInteractedRef.current = false;
      spinGlobe();
    }
  }, [showHistoryPanel]);

  useEffect(() => {
    return () => {
      if (spinFrameRef.current) cancelAnimationFrame(spinFrameRef.current);
    };
  }, []);

  const clearHistoryLayer = () => {
    if (!map.current) return;
    try {
      map.current.off("click", "history-line");
      map.current.off("mouseenter", "history-line");
      map.current.off("mouseleave", "history-line");
    } catch {}

    if (map.current.getLayer("history-line")) map.current.removeLayer("history-line");
    if (map.current.getSource("history-source")) map.current.removeSource("history-source");
    historyPointsRef.current = [];

    if (endpointMarkersRef.current.history) {
      endpointMarkersRef.current.history.start?.remove();
      endpointMarkersRef.current.history.end?.remove();
      delete endpointMarkersRef.current.history;
    }
  };

  const clearLiveRouteLayers = () => {
    if (!map.current) return;

    routeLayerIdsRef.current.forEach((id) => {
      try {
        if (map.current.getLayer(id)) map.current.removeLayer(id);
      } catch {}
      try {
        if (map.current.getSource(id)) map.current.removeSource(id);
      } catch {}
    });
    routeLayerIdsRef.current.clear();

    Object.keys(endpointMarkersRef.current).forEach((key) => {
      if (key.startsWith("live-")) {
        endpointMarkersRef.current[key].start?.remove();
        endpointMarkersRef.current[key].end?.remove();
        delete endpointMarkersRef.current[key];
      }
    });
  };

  const fetchLiveWeather = async (lat, lng) => {
    const key = window?.WEATHER_API_KEY;
    if (!key || lat == null || lng == null) return null;
    try {
      const [currRes, foreRes] = await Promise.all([
        fetch(`https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lng}&units=metric&appid=${key}`),
        fetch(`https://api.openweathermap.org/data/2.5/forecast?lat=${lat}&lon=${lng}&units=metric&appid=${key}`)
      ]);

      const curr = await currRes.json();
      const fore = await foreRes.json();

      const currentMain = curr?.weather?.[0]?.main || "";
      const currentIcon = curr?.weather?.[0]?.icon || "";

      const grouped = {};
      (fore?.list || []).forEach((item) => {
        const d = item.dt_txt?.split(" ")[0];
        if (!d) return;
        if (!grouped[d]) grouped[d] = [];
        grouped[d].push(item);
      });

      const now = new Date();
      const d1 = isoDate(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1));
      const d2 = isoDate(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 2));

      const packDay = (arr = []) => {
        if (!arr.length) return { temp: "-", desc: "N/A", main: "N/A", emoji: "🌡️" };
        const avg = Math.round(arr.reduce((s, x) => s + (x.main?.temp || 0), 0) / arr.length);
        const mid = arr[Math.floor(arr.length / 2)];
        const main = mid?.weather?.[0]?.main || "";
        const desc = mid?.weather?.[0]?.description || "N/A";
        const icon = mid?.weather?.[0]?.icon || "";
        return { temp: avg, desc, main, emoji: weatherEmoji(main, icon) };
      };

      return {
        now: {
          temp: Math.round(curr?.main?.temp ?? 0),
          desc: curr?.weather?.[0]?.description || "N/A",
          main: currentMain || "N/A",
          emoji: weatherEmoji(currentMain, currentIcon)
        },
        forecast: [{ date: d1, ...packDay(grouped[d1]) }, { date: d2, ...packDay(grouped[d2]) }]
      };
    } catch {
      return null;
    }
  };

  const fetchHistoryWeather = async (lat, lng, dateStr) => {
    const key = window?.WEATHER_API_KEY;
    if (!key || lat == null || lng == null) return null;
    try {
      const res = await fetch(
        `https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lng}&units=metric&appid=${key}`
      );
      const wx = await res.json();
      const main = wx?.weather?.[0]?.main || "";
      const icon = wx?.weather?.[0]?.icon || "";
      return {
        date: dateStr,
        temp: Math.round(wx?.main?.temp ?? 0),
        desc: wx?.weather?.[0]?.description || "N/A",
        main,
        emoji: weatherEmoji(main, icon)
      };
    } catch {
      return null;
    }
  };

  const openWorkerPopup = async (w, anchorLngLat = [w.lng, w.lat]) => {
    if (!map.current) return;
    if (currentPopup.current) currentPopup.current.remove();

    const popupContainer = document.createElement("div");
    popupContainer.style.pointerEvents = "auto";
    popupContainer.addEventListener("click", (ev) => ev.stopPropagation());

    const closeBtn = document.createElement("button");
    closeBtn.innerHTML = "&times;";
    closeBtn.style.cssText =
      "position:absolute; top:5px; right:5px; background:none; border:none; color:#aaa; font-size:24px; cursor:pointer; z-index:9999;";

    const contentDiv = document.createElement("div");
    contentDiv.style.padding = "10px";
    contentDiv.style.minWidth = "440px";
    contentDiv.style.maxWidth = "540px";

    const contentId = `summary-${w.id}-${Date.now()}`;
    const weatherId = `weather-${w.id}-${Date.now()}`;

    contentDiv.innerHTML = `
      <h3 style="margin:0 0 8px 0; font-size:16px; border-bottom:1px solid #555; padding-bottom:5px; padding-right:20px;">${w.name}</h3>
      <div id="${contentId}">
        <div style="color:#aaa; font-style:italic; font-size:12px;">Loading...</div>
      </div>
      <div id="${weatherId}" style="margin-top:8px;"></div>
    `;

    popupContainer.appendChild(closeBtn);
    popupContainer.appendChild(contentDiv);

    const popup = new mapboxgl.Popup({ offset: 25, closeButton: false })
      .setLngLat(anchorLngLat)
      .setDOMContent(popupContainer)
      .addTo(map.current);

    currentPopup.current = popup;
    closeBtn.onclick = (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      popup.remove();
      currentPopup.current = null;
    };

    try {
      const targetDiv = document.getElementById(contentId);
      const weatherDiv = document.getElementById(weatherId);

      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = pad(today.getMonth() + 1);
      const currentDay = String(today.getDate());

      const docId = `${w.id}_${currentYear}_${currentMonth}`;
      const docRef = doc(db, "monthlyWorkHours", docId);
      const docSnap = await getDoc(docRef);

      let adminPause = null;
      let adminStart = null;
      let adminEnd = null;

      if (docSnap.exists()) {
        const data = docSnap.data();
        const days = data.days || {};
        const todayData = days[currentDay];
        if (todayData) {
          if (todayData.pause) adminPause = todayData.pause;
          if (todayData.start) adminStart = todayData.start;
          if (todayData.end) adminEnd = todayData.end;
        }
      }

      const startOfDay = new Date();
      startOfDay.setHours(0, 0, 0, 0);
      const historyRef = collection(db, "location_history");
      const qDayHistory = query(
        historyRef,
        where("workerId", "==", w.id),
        where("timestamp", ">=", startOfDay),
        orderBy("timestamp", "asc")
      );
      const dayHistorySnap = await getDocs(qDayHistory);

      let morningStart = "--:--";
      if (adminStart) morningStart = adminStart;
      else if (w.activeShift && w.activeShift.startTimeISO) morningStart = w.activeShift.startTimeISO;
      else if (!dayHistorySnap.empty) morningStart = dayHistorySnap.docs[0].data().timestamp;

      let displayBreak = "00:00";
      let breakInMinutes = 0;
      if (adminPause) {
        displayBreak = adminPause;
        breakInMinutes = timeStringToMin(adminPause);
      } else if (w.activeShift) {
        let livePause = w.activeShift.totalPause || 0;
        if (w.activeShift.mode === "pause" && w.activeShift.pauseStart) {
          livePause += Math.floor(Date.now() / 1000) - w.activeShift.pauseStart;
        }
        if (livePause > 0) {
          displayBreak = formatSecondsToTime(livePause);
          breakInMinutes = Math.floor(livePause / 60);
        }
      }

      let nettoFormatted = "00:00";
      if (adminStart && adminEnd) {
        const s = timeStringToMin(adminStart);
        const e = timeStringToMin(adminEnd);
        let diff = e - s - breakInMinutes;
        if (diff < 0) diff = 0;
        nettoFormatted = `${pad(Math.floor(diff / 60))}:${pad(diff % 60)}`;
      } else if (morningStart !== "--:--") {
        let startMin = 0;
        if (String(morningStart).includes("T")) {
          const d = new Date(morningStart);
          startMin = d.getHours() * 60 + d.getMinutes();
        } else {
          startMin = timeStringToMin(formatTime(morningStart));
        }

        const nowD = new Date();
        const nowMin = nowD.getHours() * 60 + nowD.getMinutes();

        let diff = nowMin - startMin - breakInMinutes;
        if (diff < 0) diff = 0;
        nettoFormatted = `${pad(Math.floor(diff / 60))}:${pad(diff % 60)}`;
      }

      let statusText = "";
      let statusColor = "";
      const lastSeenTime = w.lastSeen || 0;
      const minutesSinceLastSeen = (Date.now() - lastSeenTime) / 1000 / 60;

      if (w.isWorking) {
        if (w.activeShift?.mode === "pause") {
          statusText = "ON BREAK";
          statusColor = "#fbbf24";
        // *** PAKEITIMAS 1: Jei active_native, nelaikome kad tai signal lost, net jei laikas skiriasi ***
        } else if (w.status !== "active_native" && minutesSinceLastSeen > 10) {
          statusText = "SIGNAL LOST";
          statusColor = "#9ca3af";
        } else {
          statusText = "ACTIVE NOW";
          statusColor = "#4ade80";
        }
      } else {
        statusText = formatTime(w.lastSeen);
        statusColor = "#f87171";
      }

      const historyTsSec = dayHistorySnap.docs.map((d) => toTsSeconds(d.data()?.timestamp)).filter((x) => x > 0);
      const nowSec = Math.floor(Date.now() / 1000);
      const { lostTotalSec, lostEvents } = calcLostStatsFromPoints(
        historyTsSec,
        nowSec,
        Boolean(w.isWorking && minutesSinceLastSeen > 10)
      );

      if (targetDiv) {
        targetDiv.innerHTML = `
          <div style="background:#1a1a1a;padding:10px;border-radius:8px;border:1px solid #333;margin-top:5px;">
            <div style="margin-bottom:8px;display:flex;justify-content:space-between;align-items:center;">
              <span style="color:#aaa;font-size:11px;font-weight:bold;">START TIME:</span>
              <span style="color:#fff;font-weight:bold;font-size:13px;">${formatTime(morningStart)}</span>
            </div>

            <div style="margin-bottom:8px;padding:6px 0;border-top:1px dashed #444;border-bottom:1px dashed #444;display:flex;justify-content:space-between;align-items:center;">
              <span style="color:#fbbf24;font-size:11px;font-weight:bold;">TOTAL BREAK (-):</span>
              <span style="color:#fbbf24;font-weight:bold;font-size:13px;">${displayBreak}</span>
            </div>

            <div style="margin-bottom:8px;padding-bottom:6px;border-bottom:1px dashed #444;display:flex;justify-content:space-between;align-items:center;">
              <span style="color:#38bdf8;font-size:11px;font-weight:bold;">NETTO (WORKED):</span>
              <span style="color:#38bdf8;font-weight:bold;font-size:13px;">${nettoFormatted} h</span>
            </div>

            <div style="margin-bottom:8px;padding-bottom:6px;border-bottom:1px dashed #444;display:flex;justify-content:space-between;align-items:center;">
              <span style="color:#9ca3af;font-size:11px;font-weight:bold;">GPS LOST TOTAL:</span>
              <span style="color:#9ca3af;font-weight:bold;font-size:13px;">${formatSecondsToTime(lostTotalSec)} (${lostEvents})</span>
            </div>

            <div style="display:flex;justify-content:space-between;align-items:center;">
              <span style="color:#aaa;font-size:11px;font-weight:bold;">CURRENT STATUS:</span>
              <span style="color:${statusColor};font-weight:bold;font-size:13px;">${statusText}</span>
            </div>
            ${
              minutesSinceLastSeen > 10 && w.isWorking
                ? `<div style="font-size:10px;color:#9ca3af;margin-top:4px;text-align:right;">Last signal: ${Math.round(minutesSinceLastSeen)} min ago</div>`
                : ""
            }
          </div>
          <div style="font-size:9px;color:#555;margin-top:6px;text-align:center;font-style:italic;">
            ${adminPause ? "Data source: Monthly Report" : "Data source: Live Shift"}
          </div>
        `;
      }

      if (weatherDiv) {
        weatherDiv.innerHTML = `<div style="font-size:12px;color:#9ca3af;">Loading weather...</div>`;
        const wx = await fetchLiveWeather(w.lat, w.lng);
        weatherDiv.innerHTML = !wx
          ? `<div style="font-size:12px;color:#f87171;">Weather unavailable</div>`
          : `
            <div style="background:#0f172a;border:1px solid #334155;border-radius:8px;padding:10px;">
              <div style="font-size:11px;color:#94a3b8;margin-bottom:4px;">NOW</div>
              <div style="font-size:14px;color:#fff;font-weight:700;">
                ${wx.now.emoji} ${wx.now.temp}°C, ${firstUpper(wx.now.desc)} (${wx.now.main})
              </div>
              <div style="margin-top:8px;border-top:1px dashed #334155;padding-top:8px;">
                <div style="font-size:11px;color:#94a3b8;margin-bottom:4px;">+2 DAYS FORECAST</div>
                ${wx.forecast
                  .map(
                    (f) => `
                    <div style="display:flex;justify-content:space-between;gap:8px;margin-bottom:4px;">
                      <span style="font-size:12px;color:#cbd5e1;">${f.date}</span>
                      <span style="font-size:12px;color:#fff;">${f.emoji} ${f.temp}°C, ${firstUpper(f.desc)} (${f.main})</span>
                    </div>
                  `
                  )
                  .join("")}
              </div>
            </div>
          `;
      }
    } catch {
      const targetDiv = document.querySelector(`[id^="summary-${w.id}"]`);
      if (targetDiv) targetDiv.innerHTML = `<div style="color:red; font-size:12px;">Error loading data</div>`;
    }
  };

  const addRouteEndpoints = (key, coords, worker = null) => {
    if (!map.current || !coords || coords.length < 1) return;

    if (endpointMarkersRef.current[key]) {
      endpointMarkersRef.current[key].start?.remove();
      endpointMarkersRef.current[key].end?.remove();
      delete endpointMarkersRef.current[key];
    }

    const now = Date.now();
    const minutesSinceLastSeen = worker ? (now - (worker.lastSeen || 0)) / 1000 / 60 : 0;

    let startColor = "rgba(34,197,94,0.55)";
    if (worker && worker.isWorking && minutesSinceLastSeen > 10) {
      startColor = "rgba(107,114,128,0.70)";
    }

    const mk = (color, size = 13) => {
      const el = document.createElement("div");
      el.style.width = `${size}px`;
      el.style.height = `${size}px`;
      el.style.borderRadius = "999px";
      el.style.background = color;
      el.style.border = "2px solid #fff";
      el.style.boxShadow = "0 0 0 2px rgba(0,0,0,0.25)";
      el.style.cursor = "pointer";
      return el;
    };

    const start = coords[0];
    const end = coords[coords.length - 1];

    const startEl = mk(startColor, 13);
    const startMarker = new mapboxgl.Marker({ element: startEl }).setLngLat(start).addTo(map.current);

    let endMarker = null;
    if (!worker || !worker.isWorking) {
      const endEl = mk("#ef4444", 13);
      endMarker = new mapboxgl.Marker({ element: endEl }).setLngLat(end).addTo(map.current);
      if (worker) {
        endEl.addEventListener("click", (e) => {
          e.stopPropagation();
          openWorkerPopup(worker, end);
        });
      }
    }

    if (worker) {
      startEl.addEventListener("click", (e) => {
        e.stopPropagation();
        openWorkerPopup(worker, start);
      });
    }

    endpointMarkersRef.current[key] = { start: startMarker, end: endMarker };
  };

  const refreshMapLayers = () => {
    if (!map.current) return;
    const isSatellite = mapStyle.includes("satellite") || map.current.getStyle().name?.includes("Satellite");

    if (isSatellite) {
      map.current.setFog({});
      if (map.current.getLayer("add-3d-buildings")) map.current.removeLayer("add-3d-buildings");
      if (map.current.getSource("add-3d-buildings")) map.current.removeSource("add-3d-buildings");
    } else {
      map.current.setFog({
        range: [0.5, 10],
        color: "rgb(186, 210, 235)",
        "high-color": "rgb(36, 92, 223)",
        "horizon-blend": 0.05,
        "space-color": "rgb(11, 11, 25)",
        "star-intensity": 0.6
      });

      if (!map.current.getLayer("add-3d-buildings")) {
        const layers = map.current.getStyle().layers || [];
        let labelLayerId;
        for (const layer of layers) {
          if (layer.type === "symbol" && layer.layout["text-field"]) {
            labelLayerId = layer.id;
            break;
          }
        }
        try {
          map.current.addLayer(
            {
              id: "add-3d-buildings",
              source: "composite",
              "source-layer": "building",
              filter: ["==", "extrude", "true"],
              type: "fill-extrusion",
              minzoom: 13,
              paint: {
                "fill-extrusion-color": "#e0e0e0",
                "fill-extrusion-height": ["get", "height"],
                "fill-extrusion-base": ["get", "min_height"],
                "fill-extrusion-opacity": 0.85
              }
            },
            labelLayerId
          );
        } catch {}
      }
    }

    enablePOIInteraction();
    if (!showHistoryPanel && Object.keys(liveRouteData).length > 0) drawRoutes(liveRouteData);
  };

  const enablePOIInteraction = () => {
    map.current.off("click", "poi-label");
    map.current.off("mouseenter", "poi-label");
    map.current.off("mouseleave", "poi-label");

    map.current.on("mouseenter", "poi-label", () => {
      map.current.getCanvas().style.cursor = "pointer";
    });
    map.current.on("mouseleave", "poi-label", () => {
      map.current.getCanvas().style.cursor = "";
    });

    map.current.on("click", "poi-label", async (e) => {
      if (currentPopup.current) {
        currentPopup.current.remove();
        currentPopup.current = null;
      }

      const feature = e.features?.[0];
      if (!feature) return;
      const name = feature.properties?.name || "Unknown Place";
      const rawCategory = feature.properties?.class || feature.properties?.maki || "Point of Interest";
      const lng = e.lngLat.lng;
      const lat = e.lngLat.lat;

      const closeBtn = document.createElement("button");
      closeBtn.innerHTML = "&times;";
      closeBtn.className = "custom-close-btn";
      closeBtn.style.cssText =
        "position:absolute; top:5px; right:5px; background:none; border:none; color:#fff; font-size:24px; cursor:pointer; z-index:9999; pointer-events:auto;";

      const contentDiv = document.createElement("div");
      contentDiv.style.padding = "10px";
      contentDiv.style.minWidth = "220px";
      contentDiv.innerHTML = `
          <div style="margin-right: 20px;">
              <h4 style="margin:0 0 4px 0; font-size:15px; color:#38bdf8; font-weight:700;">${name}</h4>
              <div class="category-content" style="font-size:11px; color:#4ade80; text-transform:uppercase; font-weight:bold; margin-bottom:6px;">${String(
                rawCategory
              ).replace(/_/g, " ")}</div>
              <div class="address-content" style="font-size:12px; color:#ccc; border-top:1px solid #333; padding-top:6px;"><span style="opacity: 0.7;">Fetching details...</span></div>
          </div>`;

      const popupContainer = document.createElement("div");
      popupContainer.appendChild(closeBtn);
      popupContainer.appendChild(contentDiv);
      popupContainer.style.pointerEvents = "auto";
      popupContainer.addEventListener("click", (ev) => ev.stopPropagation());

      const popup = new mapboxgl.Popup({ offset: 15, className: "mapboxgl-popup", closeButton: false })
        .setLngLat(e.lngLat)
        .setDOMContent(popupContainer)
        .addTo(map.current);

      currentPopup.current = popup;
      closeBtn.onclick = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        popup.remove();
        currentPopup.current = null;
      };

      map.current.flyTo({ center: e.lngLat, zoom: 17, pitch: 50 });

      try {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${mapboxgl.accessToken}&limit=1`
        );
        const data = await response.json();
        if (currentPopup.current === popup && data.features && data.features.length > 0) {
          const place = data.features[0];
          const addressDiv = contentDiv.querySelector(".address-content");
          if (addressDiv) addressDiv.innerHTML = `<strong style="color:#fff;">${place.place_name}</strong>`;
          if (place.properties && place.properties.category) {
            const categoryDiv = contentDiv.querySelector(".category-content");
            if (categoryDiv) categoryDiv.innerText = place.properties.category.split(",")[0];
          }
        }
      } catch {}
    });
  };

  const drawRoutes = (trailsObj) => {
    if (!map.current || !map.current.isStyleLoaded()) return;

    clearLiveRouteLayers();

    Object.keys(trailsObj).forEach((wId) => {
      const rawPoints = trailsObj[wId] || [];
      if (rawPoints.length < 1) return;

      const worker = workersByIdRef.current[wId] || null;
      const baseColor = workerColor(wId);
      const lostColor = "#9ca3af";

      let points = rawPoints.map((p) => ({
        coord: [p.lng, p.lat],
        ts: p.ts || 0
      }));

      if (worker?.lng != null && worker?.lat != null) {
        const last = points[points.length - 1]?.coord;
        const wCoord = [worker.lng, worker.lat];
        if (!sameCoord(last, wCoord)) {
          points.push({
            coord: wCoord,
            ts: toTsSeconds(worker.lastSeen) || 0
          });
        }
      }

      if (points.length < 2) return;

      const normalSegments = [];
      const lostSegments = [];

      for (let i = 1; i < points.length; i += 1) {
        const a = points[i - 1];
        const b = points[i];

        const dist = distanceMeters(a.coord, b.coord);
        if (dist < 0.5) continue;

        const dt = Math.max(0, (b.ts || 0) - (a.ts || 0));
        const isLostSegment = dt >= LOST_GAP_SEC;

        const feature = {
          type: "Feature",
          properties: {},
          geometry: {
            type: "LineString",
            coordinates: [a.coord, b.coord]
          }
        };

        if (isLostSegment) lostSegments.push(feature);
        else normalSegments.push(feature);
      }

      const normalSourceId = `route-normal-${wId}`;
      const normalLayerId = `route-normal-${wId}`;
      const lostSourceId = `route-lost-${wId}`;
      const lostLayerId = `route-lost-${wId}`;

      if (normalSegments.length > 0) {
        map.current.addSource(normalSourceId, {
          type: "geojson",
          data: { type: "FeatureCollection", features: normalSegments }
        });
        map.current.addLayer({
          id: normalLayerId,
          type: "line",
          source: normalSourceId,
          layout: { "line-join": "round", "line-cap": "round" },
          paint: {
            "line-color": baseColor,
            "line-width": 4,
            "line-opacity": 0.95,
            "line-dasharray": [2, 2]
          }
        });
        routeLayerIdsRef.current.add(normalLayerId);
        routeLayerIdsRef.current.add(normalSourceId);
      }

      if (lostSegments.length > 0) {
        map.current.addSource(lostSourceId, {
          type: "geojson",
          data: { type: "FeatureCollection", features: lostSegments }
        });
        map.current.addLayer({
          id: lostLayerId,
          type: "line",
          source: lostSourceId,
          layout: { "line-join": "round", "line-cap": "round" },
          paint: {
            "line-color": lostColor,
            "line-width": 4,
            "line-opacity": 1,
            "line-dasharray": [1.2, 1.2]
          }
        });
        routeLayerIdsRef.current.add(lostLayerId);
        routeLayerIdsRef.current.add(lostSourceId);
      }

      const endpointCoords = points.map((p) => p.coord);
      addRouteEndpoints(`live-${wId}`, endpointCoords, worker);
    });
  };

  useEffect(() => {
    if (!masterId) return;
    const qWorkers = query(collection(db, "workers"), where("adminId", "==", masterId));

    const unsubscribeWorkers = onSnapshot(qWorkers, (snapshot) => {
      if (!map.current) return;
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);
      const now = Date.now();

      const currentWorkers = [];
      const everyWorker = [];
      const wb = {};

      snapshot.docs.forEach((workerDoc) => {
        const w = workerDoc.data();
        w.id = workerDoc.id;
        everyWorker.push(w);
        wb[w.id] = w;

        if (!w.isWorking && (w.lastSeen || 0) < startOfToday.getTime()) return;
        currentWorkers.push(w);

        if (showHistoryPanel) return;
        if (!(w.lat && w.lng)) return;

        const lastSeenTime = w.lastSeen || 0;
        const minutesSinceLastSeen = (now - lastSeenTime) / 1000 / 60;

        let statusClass = "marker-stopped";
        if (w.isWorking) {
          // *** PAKEITIMAS 2: Jei statusas yra active_native, IGNORUOJAM LAIKĄ ir rodom žaliai ***
          if (w.status === "active_native" || minutesSinceLastSeen <= 10) {
             statusClass = "marker-online";
          } else {
             statusClass = "marker-signal-lost";
          }
        } else {
          statusClass = "marker-stopped";
        }

        let el = markersRef.current[w.id]?.getElement() || document.createElement("div");
        const newEl = el.cloneNode(true);
        if (el.parentNode) el.parentNode.replaceChild(newEl, el);
        el = newEl;

        el.className = `custom-marker ${statusClass}`;
        el.style.borderColor = "#fff";
        if (statusClass === "marker-signal-lost") el.style.backgroundColor = "#9ca3af";
        if (statusClass === "marker-stopped") el.style.backgroundColor = "#ef4444";

        el.addEventListener("click", (ev) => {
          ev.stopPropagation();
          openWorkerPopup(w, [w.lng, w.lat]);
          map.current.flyTo({ center: [w.lng, w.lat], zoom: 18, pitch: 60, bearing: -20, speed: 1.5 });
        });

        if (markersRef.current[w.id]) {
          markersRef.current[w.id].setLngLat([w.lng, w.lat]);
          markersRef.current[w.id].remove();
        }

        markersRef.current[w.id] = new mapboxgl.Marker(el).setLngLat([w.lng, w.lat]).addTo(map.current);
      });

      workersByIdRef.current = wb;

      Object.keys(markersRef.current).forEach((id) => {
        const exists = currentWorkers.some((w) => w.id === id);
        if (!exists || showHistoryPanel) {
          markersRef.current[id].remove();
          delete markersRef.current[id];
        }
      });

      setWorkersList(currentWorkers);
      setAllWorkers(everyWorker);
    });

    return () => unsubscribeWorkers();
  }, [masterId, showHistoryPanel]);

  const drawHistoryPathOnMap = (path) => {
    if (!map.current || !path?.length) return;

    clearHistoryLayer();

    map.current.addSource("history-source", {
      type: "geojson",
      data: { type: "Feature", geometry: { type: "LineString", coordinates: path } }
    });

    map.current.addLayer({
      id: "history-line",
      type: "line",
      source: "history-source",
      layout: { "line-join": "round", "line-cap": "round" },
      paint: { "line-color": "#d946ef", "line-width": 5, "line-opacity": 0.9 }
    });

    addRouteEndpoints("history", path, null);

    map.current.on("mouseenter", "history-line", () => (map.current.getCanvas().style.cursor = "pointer"));
    map.current.on("mouseleave", "history-line", () => (map.current.getCanvas().style.cursor = ""));

    map.current.on("click", "history-line", async (e) => {
      if (!e?.lngLat || !historyPointsRef.current.length) return;

      if (currentPopup.current) {
        currentPopup.current.remove();
        currentPopup.current = null;
      }

      const click = e.lngLat;
      let best = historyPointsRef.current[0];
      let bestDist = Infinity;
      historyPointsRef.current.forEach((p) => {
        const dx = p.lng - click.lng;
        const dy = p.lat - click.lat;
        const d2 = dx * dx + dy * dy;
        if (d2 < bestDist) {
          bestDist = d2;
          best = p;
        }
      });

      const dateObj = best.ts ? new Date(best.ts) : new Date(startDate);
      const y = dateObj.getFullYear();
      const m = pad(dateObj.getMonth() + 1);
      const dKey = String(dateObj.getDate());
      const dateOnly = dateObj.toLocaleDateString("lt-LT");

      let startT = "--:--";
      let endT = "--:--";
      let pauseT = "00:00";
      let nettoT = "00:00";

      try {
        const monthSnap = await getDoc(doc(db, "monthlyWorkHours", `${selectedWorkerId}_${y}_${m}`));
        if (monthSnap.exists()) {
          const day = monthSnap.data()?.days?.[dKey];
          if (day) {
            startT = day.start || "--:--";
            endT = day.end || "--:--";
            pauseT = day.pause || "00:00";

            if (day.start && day.end) {
              let diff = timeStringToMin(day.end) - timeStringToMin(day.start) - timeStringToMin(pauseT);
              if (diff < 0) diff = 0;
              nettoT = `${pad(Math.floor(diff / 60))}:${pad(diff % 60)}`;
            }
          }
        }
      } catch {}

      const wx = await fetchHistoryWeather(click.lat, click.lng, startDate);
      const wName = allWorkers.find((w) => w.id === selectedWorkerId)?.name || "Worker";

      const wrap = document.createElement("div");
      wrap.style.minWidth = "440px";
      wrap.style.maxWidth = "540px";
      wrap.style.padding = "8px";
      wrap.innerHTML = `
            <h4 style="margin:0 0 6px 0;color:#c084fc;">${wName}</h4>
            <div style="font-size:12px;color:#d1d5db;margin-bottom:7px;">${dateOnly}</div>

            <div style="background:#111827;border:1px solid #374151;border-radius:8px;padding:10px;">
              <div style="display:flex;justify-content:space-between;margin-bottom:5px;"><span style="color:#9ca3af;font-size:11px;">START</span><span style="color:#fff;font-size:12px;">${startT}</span></div>
              <div style="display:flex;justify-content:space-between;margin-bottom:5px;"><span style="color:#9ca3af;font-size:11px;">END</span><span style="color:#fff;font-size:12px;">${endT}</span></div>
              <div style="display:flex;justify-content:space-between;margin-bottom:5px;"><span style="color:#9ca3af;font-size:11px;">BREAK</span><span style="color:#fff;font-size:12px;">${pauseT}</span></div>
              <div style="display:flex;justify-content:space-between;border-top:1px dashed #374151;padding-top:6px;">
                <span style="color:#9ca3af;font-size:11px;">NETTO (WORKED)</span>
                <span style="color:#38bdf8;font-size:12px;font-weight:700;">${nettoT} h</span>
              </div>
            </div>

            <div style="margin-top:8px;background:#0f172a;border:1px solid #334155;border-radius:8px;padding:10px;">
              <div style="font-size:11px;color:#94a3b8;">WEATHER (${startDate})</div>
              <div style="font-size:13px;color:#fff;font-weight:700;margin-top:4px;">
                ${wx ? `${wx.emoji} ${wx.temp}°C, ${firstUpper(wx.desc)} (${wx.main})` : "Unavailable"}
              </div>
            </div>
          `;

      const p = new mapboxgl.Popup({ offset: 18, closeButton: true })
        .setLngLat([click.lng, click.lat])
        .setDOMContent(wrap)
        .addTo(map.current);

      currentPopup.current = p;
    });

    const bounds = new mapboxgl.LngLatBounds(path[0], path[0]);
    path.forEach((coord) => bounds.extend(coord));
    map.current.fitBounds(bounds, { padding: 50, pitch: 50, duration: 1200 });
  };

  const fetchHistory = async () => {
    if (!selectedWorkerId || !startDate || !endDate || !masterId) {
      alert("Please select a worker and dates.");
      return;
    }

    setIsLoadingHistory(true);
    clearHistoryLayer();

    try {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);

      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);

      const q = query(
        collection(db, "location_history"),
        where("workerId", "==", selectedWorkerId),
        where("timestamp", ">=", start),
        where("timestamp", "<=", end),
        orderBy("timestamp", "asc")
      );

      const snap = await getDocs(q);
      const points = snap.docs
        .map((d) => {
          const r = d.data();
          return { lng: r.lng, lat: r.lat, ts: tsToMs(r.timestamp), raw: r };
        })
        .filter((x) => x.lng != null && x.lat != null);

      historyPointsRef.current = points;
      const path = points.map((p) => [p.lng, p.lat]);

      if (!path.length) {
        alert("No data found.");
        return;
      }

      runWhenMapStyleReady(() => {
        drawHistoryPathOnMap(path);
      });
    } catch {
      alert("Error fetching history.");
    } finally {
      setIsLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (!masterId) return;
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    const qHistoryToday = query(
      collection(db, "location_history"),
      where("adminId", "==", masterId),
      where("timestamp", ">=", startOfToday),
      orderBy("timestamp", "asc")
    );

    const unsubscribeTrails = onSnapshot(qHistoryToday, (snapshot) => {
      const trailsObj = {};
      snapshot.docs.forEach((dSnap) => {
        const d = dSnap.data();
        if (d.workerId && d.lat && d.lng) {
          if (!trailsObj[d.workerId]) trailsObj[d.workerId] = [];
          trailsObj[d.workerId].push({
            lng: d.lng,
            lat: d.lat,
            ts: toTsSeconds(d.timestamp)
          });
        }
      });
      setLiveRouteData(trailsObj);
    });
    return () => unsubscribeTrails();
  }, [masterId]);

  useEffect(() => {
    if (!map.current || !isMapLoaded || !map.current.isStyleLoaded()) return;

    if (showHistoryPanel) {
      clearLiveRouteLayers();
    } else {
      clearHistoryLayer();
      if (Object.keys(liveRouteData).length > 0) drawRoutes(liveRouteData);
    }
  }, [liveRouteData, isMapLoaded, mapStyle, showHistoryPanel]);

  const toggleStyle = () => {
    if (mapStyle.includes("satellite")) setMapStyle("mapbox://styles/mapbox/streets-v12");
    else setMapStyle("mapbox://styles/mapbox/satellite-streets-v12");
  };

  useEffect(() => {
    if (map.current) map.current.setStyle(mapStyle);
  }, [mapStyle]);

  return (
    <div className="maps-page">
      <div className="maps-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate("/admin")}>
            ← Back
          </button>
        </div>
        <div className="header-center">
          <img src={bfLogo} alt="BF" className="maps-logo" />
          <span className="live-badge">3D LIVE VIEW</span>
        </div>
        <div className="header-right">
          <button className="style-toggle-btn" onClick={toggleStyle}>
            <Layers size={14} /> {mapStyle.includes("satellite") ? "Standard Map" : "Satellite"}
          </button>
          <button className="history-toggle-btn" onClick={() => setShowHistoryPanel(!showHistoryPanel)}>
            {showHistoryPanel ? <MapIcon size={14} /> : <History size={14} />}
            {showHistoryPanel ? "Live View" : "History"}
          </button>
        </div>
      </div>

      <div className="map-content-wrapper">
        {showHistoryPanel && (
          <div className="history-sidebar">
            <h3>Route History</h3>
            <div className="sidebar-field">
              <label>Worker</label>
              <select value={selectedWorkerId} onChange={(e) => setSelectedWorkerId(e.target.value)}>
                <option value="">-- Select --</option>
                {allWorkers.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="sidebar-field">
              <label>From</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="sidebar-field">
              <label>To</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
            <button className="fetch-btn" onClick={fetchHistory} disabled={isLoadingHistory}>
              {isLoadingHistory ? "Loading..." : "Show Route"}
            </button>
          </div>
        )}

        <div className="map-wrapper">
          <div ref={mapContainer} className="map-container" />

          <div
            style={{
              position: "absolute",
              top: 14,
              right: 14,
              zIndex: 12,
              background: "rgba(51,65,85,0.82)",
              color: "#e2e8f0",
              borderRadius: 12,
              padding: "8px 12px",
              fontSize: 12,
              fontWeight: 700
            }}
          >
            {topDateText} | KW {topKW}
          </div>

          <div className="map-legend-overlay">
            <div className="legend-item">
              <div className="dot green"></div> Work
            </div>
            <div className="legend-item">
              <div className="dot gray"></div> Signal Lost
            </div>
            <div className="legend-item">
              <div className="dot red"></div> End
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}